import { useMemo, useState } from 'react';
import { Search, Loader2, Building2, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useMerchantMarket } from '../hooks/useMerchantMarket';
import MarketHero from '../components/marketplace/MarketHero';
import MarketFilterShell from '../components/marketplace/MarketFilterShell';
import EmptyState from '../components/marketplace/EmptyState';
import PropertyCard from '../components/marketplace/PropertyCard';
import { pageBg, inputCls, selectCls } from '../components/marketplace/frames';

const EMIRATES = [
  ['dubai', 'دبي', 'Dubai'],
  ['abu_dhabi', 'أبوظبي', 'Abu Dhabi'],
  ['sharjah', 'الشارقة', 'Sharjah'],
  ['ajman', 'عجمان', 'Ajman'],
  ['ras_al_khaimah', 'رأس الخيمة', 'Ras Al Khaimah'],
  ['umm_al_quwain', 'أم القيوين', 'Umm Al Quwain'],
  ['fujairah', 'الفجيرة', 'Fujairah'],
];

export default function RealEstatePage() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const { items, loading, error } = useMerchantMarket('real_estate', { limit: 300 });
  const [q, setQ] = useState('');
  const [listingType, setListingType] = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  const [emirate, setEmirate] = useState('all');
  const [bedrooms, setBedrooms] = useState('all');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sort, setSort] = useState('newest');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const filtered = useMemo(() => {
    let list = items;
    if (listingType !== 'all') list = list.filter(i => i.listingType === listingType);
    if (propertyType !== 'all') list = list.filter(i => i.propertyType === propertyType);
    if (emirate !== 'all') list = list.filter(i => (i.emirate || '').toLowerCase() === emirate);
    if (bedrooms !== 'all') {
      const b = parseInt(bedrooms, 10);
      list = list.filter(i => bedrooms === '5+' ? (i.bedrooms || 0) >= 5 : (i.bedrooms || 0) === b);
    }
    const pMin = parseFloat(priceMin);
    const pMax = parseFloat(priceMax);
    if (!Number.isNaN(pMin)) list = list.filter(i => !i.priceOnRequest && (i.salePrice || i.price || 0) >= pMin);
    if (!Number.isNaN(pMax)) list = list.filter(i => !i.priceOnRequest && (i.salePrice || i.price || 0) <= pMax);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(i =>
        (i.title || '').toLowerCase().includes(s) ||
        (i.location || '').toLowerCase().includes(s) ||
        (i.area_name || '').toLowerCase().includes(s) ||
        (i.building_name || '').toLowerCase().includes(s) ||
        (i.store?.name || '').toLowerCase().includes(s),
      );
    }
    if (sort === 'newest')          list = [...list].sort((a,b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sort === 'price_asc')  list = [...list].filter(i => !i.priceOnRequest).sort((a,b) => (a.salePrice||a.price||0) - (b.salePrice||b.price||0));
    else if (sort === 'price_desc') list = [...list].filter(i => !i.priceOnRequest).sort((a,b) => (b.salePrice||b.price||0) - (a.salePrice||a.price||0));
    return list;
  }, [items, q, listingType, propertyType, emirate, bedrooms, priceMin, priceMax, sort]);

  const hasAnyFilter = q || listingType !== 'all' || propertyType !== 'all' || emirate !== 'all' || bedrooms !== 'all' || priceMin || priceMax || sort !== 'newest';
  const clearAll = () => {
    setQ(''); setListingType('all'); setPropertyType('all'); setEmirate('all');
    setBedrooms('all'); setPriceMin(''); setPriceMax(''); setSort('newest');
  };
  const inp = inputCls(isDarkMode);
  const sel = selectCls(isDarkMode);

  return (
    <div className={`min-h-screen ${pageBg(isDarkMode)}`} dir={isAr ? 'rtl' : 'ltr'}>
      <MarketHero
        isDarkMode={isDarkMode}
        isAr={isAr}
        icon={Building2}
        eyebrowAr="أكبر مجموعة عقارية موثوقة"
        eyebrowEn="Largest trusted property portfolio"
        titleAr="عقارات"
        titleAccentAr="للبيع والإيجار"
        titleEn="Real Estate"
        titleAccentEn="Buy & Rent"
        subtitleAr="اكتشف أفضل العقارات في جميع أنحاء الإمارات من فلل وشقق ومكاتب وأراضٍ — بأسعار وشروط تنافسية من وسطاء معتمدين."
        subtitleEn="Discover premium properties across the UAE — villas, apartments, offices and land — at competitive prices from licensed brokers."
        aiPromptAr="أبحث عن عقار مناسب — ساعدني في تحديد المنطقة والميزانية"
        aiPromptEn="I'm looking for a property — help me pick the area and budget"
        stats={[
          { value: items.length || '—', label: isAr ? 'عقار متاح' : 'Available', icon: <Building2 className="h-4 w-4" /> },
          { value: '7', label: isAr ? 'إمارات' : 'Emirates', accent: '#F5A962' },
          { value: '✓', label: isAr ? 'موثّق' : 'Verified', accent: '#1E9BD9' },
        ]}
      />

      <MarketFilterShell
        isDarkMode={isDarkMode}
        isAr={isAr}
        count={filtered.length}
        total={items.length}
        unitAr="عقار"
        unitEn="properties"
        hasFilter={hasAnyFilter}
        onClear={clearAll}
        testId="realestate-filters"
      >
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[#1E9BD9]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAr ? 'العنوان، المنطقة، البناية…' : 'Title, area, building…'} className={`h-11 ps-9 rounded-xl ${inp}`} data-testid="realestate-search" />
          </div>
          <select value={listingType} onChange={(e) => setListingType(e.target.value)} className={sel} data-testid="realestate-listing">
            <option value="all">{isAr ? 'بيع + إيجار' : 'Sale + Rent'}</option>
            <option value="for_sale">{isAr ? 'للبيع' : 'For Sale'}</option>
            <option value="for_rent">{isAr ? 'للإيجار' : 'For Rent'}</option>
            <option value="for_investment">{isAr ? 'للاستثمار' : 'For Investment'}</option>
          </select>
          <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className={sel} data-testid="realestate-property">
            <option value="all">{isAr ? 'كل الأنواع' : 'All Types'}</option>
            <option value="apartment">{isAr ? 'شقة' : 'Apartment'}</option>
            <option value="villa">{isAr ? 'فيلا' : 'Villa'}</option>
            <option value="townhouse">{isAr ? 'تاون هاوس' : 'Townhouse'}</option>
            <option value="office">{isAr ? 'مكتب' : 'Office'}</option>
            <option value="shop">{isAr ? 'محل' : 'Shop'}</option>
            <option value="warehouse">{isAr ? 'مستودع' : 'Warehouse'}</option>
            <option value="land">{isAr ? 'أرض' : 'Land'}</option>
          </select>
          <Button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className={`h-11 rounded-xl font-semibold whitespace-nowrap ${
              showAdvanced
                ? 'bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] text-white hover:opacity-90'
                : isDarkMode
                  ? 'bg-white/5 text-white border border-[#1E9BD9]/20 hover:bg-[#1E9BD9]/10'
                  : 'bg-white text-slate-700 border border-[#1E9BD9]/25 hover:bg-[#1E9BD9]/5'
            }`}
            data-testid="realestate-advanced-toggle"
          >
            <SlidersHorizontal className="h-4 w-4 me-1.5" />
            {isAr ? 'خيارات أكثر' : 'More filters'}
            <ChevronDown className={`h-4 w-4 ms-1.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        {showAdvanced && (
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <select value={emirate} onChange={(e) => setEmirate(e.target.value)} className={sel} data-testid="realestate-emirate">
              <option value="all">{isAr ? 'كل الإمارات' : 'All Emirates'}</option>
              {EMIRATES.map(([k, ar, en]) => <option key={k} value={k}>{isAr ? ar : en}</option>)}
            </select>
            <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className={sel} data-testid="realestate-bedrooms">
              <option value="all">{isAr ? 'الغرف: الكل' : 'Bedrooms: Any'}</option>
              {['1','2','3','4','5+'].map(n => <option key={n} value={n}>{n} {isAr ? 'غرف' : 'BR'}</option>)}
            </select>
            <Input value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder={isAr ? 'السعر من (AED)' : 'Min price (AED)'} className={`h-11 rounded-xl ${inp}`} type="number" data-testid="realestate-price-min" />
            <Input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder={isAr ? 'السعر إلى (AED)' : 'Max price (AED)'} className={`h-11 rounded-xl ${inp}`} type="number" data-testid="realestate-price-max" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={`${sel} col-span-2 md:col-span-4`} data-testid="realestate-sort">
              <option value="newest">{isAr ? 'الترتيب: الأحدث' : 'Sort: Newest'}</option>
              <option value="price_asc">{isAr ? 'السعر: منخفض → مرتفع' : 'Price: Low → High'}</option>
              <option value="price_desc">{isAr ? 'السعر: مرتفع → منخفض' : 'Price: High → Low'}</option>
            </select>
          </div>
        )}
      </MarketFilterShell>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        ) : error ? (
          <p className="text-center text-red-500 py-24">{error}</p>
        ) : filtered.length === 0 ? (
          <EmptyState isDarkMode={isDarkMode} icon={Building2} title={isAr ? 'لا توجد عقارات مطابقة' : 'No matching properties'} hint={isAr ? 'جرّب تغيير الفلاتر أو البحث بكلمات أخرى' : 'Try adjusting filters or search terms'} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="realestate-grid">
            {filtered.map((it, idx) => <PropertyCard key={it.id} item={it} index={idx} isDarkMode={isDarkMode} isAr={isAr} />)}
          </div>
        )}
      </section>
    </div>
  );
}
